/**
 * Trigger Rules Execution System
 * Evaluates conditions and executes webhooks for trigger rules
 */

import { TriggerRule, TriggerCondition } from '@/types/theme';
import { webhookLogger } from './webhookLogger';
import { supabase } from '@/integrations/supabase/client';

// Interface for parsed webhook provider information
interface WebhookProvider {
  id: string;
  url: string;
  name?: string;
}

// Temporary console logging instead of chatLogger for now
const logger = {
  debug: (message: string) => console.log('[DEBUG]', message),
  info: (message: string) => console.log('[INFO]', message),
  error: (message: string, error?: any) => console.error('[ERROR]', message, error),
  conversation: (message: string, data?: any) => console.log('[CONVERSATION]', message, data)
};

export interface TriggerExecutionContext {
  chatbotId: string;
  sessionId: string;
  userResponses: Record<string, any>;
  messages: any[];
  conversationState: any;
  questions?: any[]; // Add questions for shortname mapping
  conversationId?: string; // Add conversation ID for webhook logging
}

export class TriggerExecutor {
  // Track executed triggers per session to prevent duplicates
  private static executedTriggers: Map<string, Set<string>> = new Map();

  /**
   * Check if a trigger has already been executed for this session
   */
  private static hasTriggeredInSession(sessionId: string, ruleId: string): boolean {
    const sessionTriggers = this.executedTriggers.get(sessionId);
    return sessionTriggers ? sessionTriggers.has(ruleId) : false;
  }

  /**
   * Mark a trigger as executed for this session
   */
  private static markTriggeredInSession(sessionId: string, ruleId: string): void {
    if (!this.executedTriggers.has(sessionId)) {
      this.executedTriggers.set(sessionId, new Set());
    }
    this.executedTriggers.get(sessionId)!.add(ruleId);
  }

  /**
   * Clear session tracking (useful for testing or cleanup)
   */
  static clearSessionTracking(sessionId?: string): void {
    if (sessionId) {
      this.executedTriggers.delete(sessionId);
    } else {
      this.executedTriggers.clear();
    }
  }

  /**
   * Get session tracking stats (useful for debugging)
   */
  static getSessionTrackingStats(): { totalSessions: number; totalExecutedTriggers: number } {
    let totalExecutedTriggers = 0;
    for (const sessionTriggers of this.executedTriggers.values()) {
      totalExecutedTriggers += sessionTriggers.size;
    }

    return {
      totalSessions: this.executedTriggers.size,
      totalExecutedTriggers
    };
  }

  /**
   * Evaluate all trigger rules for a conversation
   */
  static async evaluateAndExecuteTriggers(
    triggerRules: TriggerRule[],
    context: TriggerExecutionContext
  ): Promise<void> {
    if (!triggerRules || triggerRules.length === 0) {
      logger.conversation('No trigger rules to evaluate', {
        chatbotId: context.chatbotId,
        sessionId: context.sessionId
      });
      return;
    }

    logger.conversation('Evaluating trigger rules', {
      chatbotId: context.chatbotId,
      sessionId: context.sessionId,
      ruleCount: triggerRules.length,
      userResponsesCount: Object.keys(context.userResponses).length
    });

    for (const rule of triggerRules) {
      if (!rule.active) {
        logger.conversation('Skipping inactive trigger rule', {
          ruleId: rule.id,
          ruleName: rule.name
        });
        continue;
      }

      // Fix: Parse conditions if they're stored as JSON string
      let conditions = rule.conditions;
      if (typeof conditions === 'string') {
        try {
          conditions = JSON.parse(conditions);
        } catch (parseError) {
          logger.error('Failed to parse conditions JSON string', {
            ruleId: rule.id,
            error: parseError
          });
          continue;
        }
      }

      // Check if this trigger has already been executed for this session
      if (this.hasTriggeredInSession(context.sessionId, rule.id)) {
        logger.conversation('Skipping already executed trigger rule for this session', {
          ruleId: rule.id,
          ruleName: rule.name,
          sessionId: context.sessionId
        });

        continue;
      }

      const conditionsMet = this.evaluateConditions(conditions, context.userResponses);

      logger.conversation('Trigger rule evaluation result', {
        ruleId: rule.id,
        ruleName: rule.name,
        conditionsMet,
        conditionsCount: conditions?.length || 0,
        sessionId: context.sessionId
      });

      if (conditionsMet) {
        // Mark as executed before executing to prevent race conditions
        this.markTriggeredInSession(context.sessionId, rule.id);

        logger.conversation('Executing trigger rule (first time this session)', {
          ruleId: rule.id,
          ruleName: rule.name,
          sessionId: context.sessionId
        });

        await this.executeTriggerAction(rule, context);
      }
    }
  }

  /**
   * Evaluate if all conditions in a rule are met
   */
  private static evaluateConditions(
    conditions: TriggerCondition[],
    userResponses: Record<string, any>
  ): boolean {
    if (!conditions || conditions.length === 0) {
      return true; // No conditions means always execute
    }

    for (const condition of conditions) {
      if (!this.evaluateSingleCondition(condition, userResponses)) {
        return false; // All conditions must be true (AND logic)
      }
    }

    return true;
  }

  /**
   * Evaluate a single condition
   */
  private static evaluateSingleCondition(
    condition: TriggerCondition,
    userResponses: Record<string, any>
  ): boolean {
    // Handle both regular questions and data collection fields
    let userResponse;
    let responseKey = condition.questionId;

    // Safety check for undefined questionId
    if (!condition.questionId) {
      return false;
    }

    if (condition.questionId.startsWith('data_field:')) {
      // This is a data collection field
      const fieldName = condition.questionId.replace('data_field:', '');
      // Look for AI-extracted data with the field name
      userResponse = userResponses[`ai_extracted_${fieldName}`] || userResponses[fieldName];
      responseKey = `data_field:${fieldName}`;
    } else {
      // This is a regular question
      userResponse = userResponses[condition.questionId];
    }

    if (userResponse === undefined || userResponse === null) {
      return false;
    }

    const { operator, value } = condition;

    switch (operator) {
      case 'equals':
        return String(userResponse).toLowerCase() === String(value).toLowerCase();

      case 'contains':
        return String(userResponse).toLowerCase().includes(String(value).toLowerCase());

      case 'greaterThan': {
        const numResponse = Number(userResponse);
        const numValue = Number(value);
        return !isNaN(numResponse) && !isNaN(numValue) && numResponse > numValue;
      }

      case 'lessThan': {
        const numResponseLess = Number(userResponse);
        const numValueLess = Number(value);
        return !isNaN(numResponseLess) && !isNaN(numValueLess) && numResponseLess < numValueLess;
      }

      case 'between':
        if (Array.isArray(value) && value.length === 2) {
          const numResponseBetween = Number(userResponse);
          const [min, max] = value.map(Number);
          return !isNaN(numResponseBetween) && numResponseBetween >= min && numResponseBetween <= max;
        }
        return false;

      default:
        logger.error('Unknown condition operator', {
          operator,
          questionId: condition.questionId
        });
        return false;
    }
  }

  /**
   * Execute the action for a triggered rule
   */
  private static async executeTriggerAction(
    rule: TriggerRule,
    context: TriggerExecutionContext
  ): Promise<void> {
    logger.conversation('Executing trigger action', {
      ruleId: rule.id,
      ruleName: rule.name,
      action: rule.action || 'webhook'
    });

    // Currently only webhook actions are implemented
    if (rule.action === 'webhook' || !rule.action) {
      await this.executeWebhookAction(rule, context);
    } else if (rule.action === 'goto') {
      logger.conversation('Goto action not yet implemented', {
        ruleId: rule.id,
        actionTarget: rule.actionTarget
      });
    }
  }

  /**
   * Execute webhook action for a trigger rule
   */
  private static async executeWebhookAction(
    rule: TriggerRule,
    context: TriggerExecutionContext
  ): Promise<void> {
    if (!rule.webhookUrl) {
      logger.error('Trigger rule has no webhook URL', {
        ruleId: rule.id,
        ruleName: rule.name
      });
      return;
    }

    // Get PII data if available
    let piiData = null;
    try {
      const piiResponse = await fetch(`/api/pii/${context.sessionId}`);
      if (piiResponse.ok) {
        piiData = await piiResponse.json();
        logger.conversation('PII data retrieved for webhook', {
          sessionId: context.sessionId,
          ruleId: rule.id,
          piiFields: Object.keys(piiData || {})
        });
      } else if (piiResponse.status === 404) {
        // PII data not found - this is normal, not an error
        logger.conversation('No PII data found for session', {
          sessionId: context.sessionId,
          ruleId: rule.id
        });
      } else {
        logger.error('PII API returned error', {
          sessionId: context.sessionId,
          ruleId: rule.id,
          status: piiResponse.status,
          statusText: piiResponse.statusText
        });
      }
    } catch (piiError) {
      // Only log as warning since PII data is optional
      logger.conversation('Could not fetch PII data for trigger webhook', {
        sessionId: context.sessionId,
        ruleId: rule.id,
        error: piiError instanceof Error ? piiError.message : 'Unknown error'
      });
    }

    // Parse webhook providers (handle both single URLs and multiple PingPost providers)
    const webhookProviders = await this.parseWebhookProviders(rule.webhookUrl, context.chatbotId);

    // Execute webhook(s) with logging
    const results = await Promise.allSettled(
      webhookProviders.map(async (provider) => {
        try {
          const triggerName = provider.name
            ? `trigger_${rule.name}_${provider.name}`
            : `trigger_${rule.name}`;

          // Prepare provider-specific payload using provider-specific mappings
          const providerPayload = await this.prepareWebhookPayload(rule, context, piiData, provider.id);

          return await webhookLogger.sendWebhookWithLogging(
            context.chatbotId,
            context.sessionId,
            provider.url,
            providerPayload,
            piiData,
            triggerName,
            rule.id, // Pass the actual trigger rule ID
            context.conversationId // Pass conversation ID if available
          );
        } catch (error) {
          logger.error('Failed to execute webhook to provider', {
            ruleId: rule.id,
            ruleName: rule.name,
            providerName: provider.name || 'Unknown',
            webhookUrl: provider.url,
            error: error instanceof Error ? error.message : 'Unknown error'
          });
          throw error;
        }
      })
    );

    // Log overall results
    const successful = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;

    const providerNames = webhookProviders.map(p => p.name || p.url).join(', ');

    logger.conversation('Trigger webhook execution completed', {
      ruleId: rule.id,
      ruleName: rule.name,
      totalProviders: webhookProviders.length,
      successful,
      failed,
      providers: providerNames
    });

    if (failed > 0) {
      logger.error('Some webhook executions failed', {
        ruleId: rule.id,
        ruleName: rule.name,
        failed,
        total: webhookProviders.length,
        providers: providerNames
      });
    }
  }

  /**
   * Parse webhook providers from rule.webhookUrl and fetch provider names from database
   */
  private static async parseWebhookProviders(webhookUrl: string, chatbotId: string): Promise<WebhookProvider[]> {
    if (!webhookUrl) return [];

    try {
      // Try to parse as JSON array (PingPost providers)
      const parsed = JSON.parse(webhookUrl);
      if (Array.isArray(parsed) && parsed.every(p => p.id && p.url)) {
        // Extract provider IDs to fetch names from database
        const providerIds = parsed.map(p => p.id);

        // Fetch provider names from database
        const { data: providers, error } = await supabase
          .from('pingpost_providers')
          .select('id, name, url')
          .in('id', providerIds);

        if (error) {
          logger.error('Failed to fetch provider names from database', { error: error.message });
          // Fallback to using URLs without names
          return parsed.map(p => ({ id: p.id, url: p.url }));
        }

        // Map providers with names
        return parsed.map(p => {
          const dbProvider = providers?.find(dbP => dbP.id === p.id);
          return {
            id: p.id,
            url: p.url,
            name: dbProvider?.name || `Provider ${p.id.substring(0, 8)}`
          };
        });
      }
    } catch (e) {
      // Not JSON, treat as single URL
    }

    // Single URL (custom webhook or single provider)
    return [{ id: 'custom', url: webhookUrl, name: 'Custom Webhook' }];
  }

  /**
   * Convert responses from question IDs to POST variable mappings
   */
  private static async convertResponsesToPostVariables(
    userResponses: Record<string, any>,
    postVariableMappings: any[],
    chatbotId: string
  ): Promise<Record<string, any>> {
    const mappedResponses: Record<string, any> = {};

    // Create a map of question ID to POST variable name
    const questionToVariableMap = postVariableMappings.reduce((map, mapping) => {
      if (mapping.questionId && mapping.variableName) {
        map[mapping.questionId] = mapping.variableName;
      }
      return map;
    }, {} as Record<string, string>);

    // Get question shortnames for fallback
    // Filter out invalid question IDs (must be valid UUIDs)
    // Updated regex to be more permissive for test UUIDs with zeros
    const validUuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const allQuestionIds = Object.keys(userResponses);
    const questionIds = allQuestionIds.filter(id => validUuidRegex.test(id));
    let questionShortnameMap: Record<string, string> = {};

    if (questionIds.length > 0) {
      try {
        const { supabase } = await import('@/integrations/supabase/client');
        const { data: questions, error } = await supabase
          .from('questions')
          .select('id, shortname')
          .eq('chatbot_id', chatbotId)
          .in('id', questionIds);

        if (!error && questions) {
          questionShortnameMap = questions.reduce((map, question) => {
            if (question.shortname) {
              map[question.id] = question.shortname;
            }
            return map;
          }, {} as Record<string, string>);

        } else if (error) {
          console.warn('Error fetching question shortnames:', error);
        }
      } catch (error) {
        console.warn('Could not fetch question shortnames for fallback:', error);
      }
    }

    // Convert responses to use POST variable names
    Object.entries(userResponses).forEach(([questionId, response]) => {
      const variableName = questionToVariableMap[questionId];

      if (variableName) {
        // Use explicit POST variable mapping
        mappedResponses[variableName] = response;
      } else {
        // Fallback to shortname if available
        const shortname = questionShortnameMap[questionId];
        if (shortname) {
          mappedResponses[shortname] = response;
        } else {
          // Final fallback to question ID
          mappedResponses[questionId] = response;
        }
      }
    });

    return mappedResponses;
  }

  /**
   * Prepare webhook payload with variable mappings and PII data
   */
  private static async prepareWebhookPayload(
    rule: TriggerRule,
    context: TriggerExecutionContext,
    piiData?: Record<string, any>,
    providerId?: string
  ): Promise<Record<string, any>> {
    // Convert responses to use POST variable mappings with shortname fallback
    let mappedResponses: Record<string, any>;

    if (rule.postVariableMappings && rule.postVariableMappings.length > 0) {
      // Filter mappings for this specific provider (or global mappings if no providerId)
      const providerMappings = rule.postVariableMappings.filter(mapping => {
        if (providerId) {
          // For specific provider: use provider-specific mappings, fallback to global mappings
          return mapping.providerId === providerId || !mapping.providerId;
        } else {
          // For custom webhooks: use only global mappings (no providerId)
          return !mapping.providerId;
        }
      });

      // Use filtered POST variable mappings for responses object with shortname fallback
      mappedResponses = await this.convertResponsesToPostVariables(
        context.userResponses,
        providerMappings,
        context.chatbotId
      );
    } else {
      // No explicit mappings - use shortname fallback for all questions
      mappedResponses = await this.convertResponsesToPostVariables(
        context.userResponses,
        [], // Empty mappings array
        context.chatbotId
      );
    }

    // Merge PII data into the payload if available
    let finalPayload = { ...mappedResponses };

    if (piiData && typeof piiData === 'object' && Object.keys(piiData).length > 0) {
      // Add PII data to the payload
      finalPayload = {
        ...mappedResponses,
        ...piiData
      };

    }

    // Return the complete payload with POST variables, shortnames, and PII data
    return finalPayload;
  }
}

// Export default instance
export const triggerExecutor = TriggerExecutor;
